import React, {
  lazy, Suspense, useCallback, useEffect, useState,
} from 'react';
import Box from '@mui/material/Box';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import {
  doc, getDoc,
  disableNetwork,
  enableNetwork,
  addDoc,
  collection,
} from 'firebase/firestore';
import useGlobal from 'global-state/store';
import { MenuProvider } from 'menu-actions/MenuContext';
import {
  BrowserRouter, Routes, Route, useNavigate, useLocation,
  Navigate,
} from 'react-router-dom';
import MyAccount from 'components/account/MyAccount';
import cgu from 'staticFiles/CGU.pdf';
import cgv from 'staticFiles/CGV.pdf';
import cgucgv from 'staticFiles/CGU+CGV.pdf';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import { useTranslation } from 'react-i18next';
import GlobalSnackbar from 'components/GlobalSnackbar';
import { Cookies, Contact } from 'labox-ws-commons';
import { getAnalytics } from 'firebase/analytics';
import RemoteConfigStatus from 'components/RemoteConfigStatus';
import LoadingPage from 'components/LoadingPage';
import privacyFR from 'staticFiles/privacy-policy-fr.pdf';
import privacyEN from 'staticFiles/privacy-policy-en.pdf';
import { useAuth, useSignInCheck } from 'firebaseHooks/AuthContext';
import { inventoryRoles } from 'utils/inventoryRoles';
import AdminOnlineGate from './admin/AdminOnlineGate';
import ScrollToTop from './ScrollToTop';
import Footer from './Footer';
import TopButtons from './TopButtons';
import SignInPage from './SignInPage';
import SelectInventory from './operation/SelectInventory';
import Inventory from './operation/Inventory';
import Operation from './operation/Operation';
import { getValidAccessGrants, userInventoryAppAccess } from './utils';
import AdminAccessGate from './admin/AdminAccessGate';
import NotFound from './NotFound';
import { normalizeHiddenColumns } from './admin/inventories/supervisorColumns';

const StoragePDFViewerPage = lazy(() => import('components/StoragePDFViewerPage'));
const PDFView = lazy(() => import('./PDFView'));
const Admin = lazy(() => import('./admin/Admin'));
const CreateInventory = lazy(() => import('./admin/inventory/CreateInventory'));
const Inventories = lazy(() => import('./admin/inventories/Inventories'));
const Incidents = lazy(() => import('./admin/incidents/Incidents'));
const InventoryDetail = lazy(() => import('./admin/inventories/InventoryDetail'));
const InventoryHistory = lazy(() => import('./admin/inventories/InventoryHistory'));

export default function Home() {
  return <RemoteConfigStatus defaultComponent={<UserSetupMenuAndMainPage />} />;
}

function UserSetupMenuAndMainPage() {
  const { currentUser: user } = useAuth();
  const db = useFirestore();
  const [globalState, globalActions] = useGlobal();
  const { status: signInStatus, data: signInCheckResult } = useSignInCheck();

  const getAlcoholometryAccessGrantsCb = useCallback(async (activeOrganization) => getValidAccessGrants(
    db,
    user,
    activeOrganization,
  ), [db, user]);

  useEffect(() => {
    // eslint-disable-next-line no-promise-executor-return
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    async function getUser(uid) {
      const userRef = doc(db, 'users', uid);
      const firestoreUser = await getDoc(userRef);
      return firestoreUser;
    }
    async function getUserPrivateData(uid) {
      const userRef = doc(db, 'users', uid, 'private', 'data');
      const firestoreUser = await getDoc(userRef);
      return firestoreUser;
    }
    async function getMemberRole(organizationId, uid) {
      try {
        const member = await getDoc(doc(db, 'organizations', organizationId, 'members', uid));
        return member.data()?.role;
      } catch (err) {
        // Labox WS admins may not be members; their role comes from laboxWSRoles.
        return undefined;
      }
    }
    async function fetchMultipleTimesUser(uid) {
      let firestoreUser = await getUser(uid);
      if (!firestoreUser.exists()) {
        await sleep(1000);
        firestoreUser = await getUser(uid);
        if (!firestoreUser.exists()) {
          await await sleep(2000);
          firestoreUser = await getUser(uid);
        }
      }
      return firestoreUser;
    }

    async function setupGlobalStateFromUser() {
      globalActions.setUserStatus('loading');
      // Organization switches rerun this setup; never keep roles from the previous organization.
      globalActions.setUserRoles([]);
      const firestoreUser = await fetchMultipleTimesUser(user.uid);
      const userData = firestoreUser.data();

      // Check for global super admin role in laboxWSRoles
      const isSuperAdmin = Array.isArray(userData?.laboxWSRoles) && userData.laboxWSRoles.includes('admin');
      globalActions.setSuperAdmin(isSuperAdmin);

      const firestoreUserPrivateData = await getUserPrivateData(user.uid);
      const userPrivateData = firestoreUserPrivateData.data();
      globalActions.setActiveOrganization(userPrivateData.activeOrganization);

      const accessGrants = await getAlcoholometryAccessGrantsCb(userPrivateData.activeOrganization);
      globalActions.setAccessGrants(accessGrants.validGrants);
      globalActions.setAccessGrantNames(accessGrants.uniqueGrantNames);

      const access = userInventoryAppAccess(accessGrants.uniqueGrantNames);

      if (userPrivateData.activeOrganization === '' || access === 'none') {
        return globalActions.setUserStatus('notYetSetup');
      }
      const orgaAppPath = `/organizations/${userPrivateData.activeOrganization}/apps/inventory`;
      const organizationAppRef = doc(db, orgaAppPath);
      const firestoreorganizationApp = await getDoc(organizationAppRef);
      const organizationAppData = firestoreorganizationApp.data();
      globalActions.setClientPath(orgaAppPath);
      if (!organizationAppData) {
        return globalActions.setUserStatus('notYetSetup');
      }
      // Roles are read once at startup: a role change applies after a reload.
      const memberRole = await getMemberRole(userPrivateData.activeOrganization, user.uid);
      globalActions.setUserRoles(inventoryRoles({
        userId: user.uid, appData: organizationAppData, memberRole, isSuperAdmin,
      }));
      // the following if case can be removed once all orgs have migrated to use csvMappings
      if (organizationAppData.csvMappings === undefined && organizationAppData.defaultBarrelCsvMapping !== undefined) {
        globalActions.setCsvMappings({
          default: {
            ...organizationAppData.defaultBarrelCsvMapping,
            timestamp: new Date(),
          },
        });
      }
      if (organizationAppData.csvMappings !== undefined) {
        globalActions.setCsvMappings(organizationAppData.csvMappings);
      }
      globalActions.setSupervisorHiddenColumns(
        normalizeHiddenColumns(organizationAppData.supervisorHiddenColumns),
      );
      if (organizationAppData.gaugerCollaborations !== undefined) {
        globalActions.setGaugerCollaborations(organizationAppData.gaugerCollaborations);
      }
      return globalActions.setUserStatus('setupDone');
    }

    function setupFirestoreNetwork() {
      if (globalState.networkState === 'offline') {
        disableNetwork(db);
      }
    }

    if (signInStatus === 'success'
      && user?.uid !== undefined
      && globalState.userStatus === 'initial') {
      setupGlobalStateFromUser();
      setupFirestoreNetwork();
    } else if (signInStatus === 'success' && signInCheckResult.signedIn === false) {
      globalActions.setUserStatus('notSignedIn');
    }
  }, [db, getAlcoholometryAccessGrantsCb, globalActions, globalState.networkState,
    globalState.userStatus, user?.uid, signInStatus, signInCheckResult.signedIn]);

  window.ononline = (event) => {
    enableNetwork(db);
    globalActions.setNetworkState('online');
  };

  window.onoffline = (event) => {
    disableNetwork(db);
    globalActions.setNetworkState('offline');
  };

  if (signInStatus === 'loading') {
    return (
      <LoadingPage />
    );
  }

  return <MenuProvider app={<MainPage />} />;
}

function MainPage() {
  const [, globalActions] = useGlobal();
  const { t } = useTranslation();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <GlobalOfflineBanner />
        <Box sx={{
          minHeight: '80vh', display: 'flex', flexDirection: 'column',
        }}
        >
          <GlobalSnackbar />
          <TopButtons />
          <Suspense fallback={<LoadingPage />}>
            <MainPageContent />
          </Suspense>
        </Box>
        <Footer />
        <Cookies
          cookieName="agreed-to-cookies-on-labox-inventory"
          t={t}
          globalActions={globalActions}
        />
      </Box>
    </BrowserRouter>
  );
}

// Displays the global offline banner except on inventory routes
function GlobalOfflineBanner() {
  const [globalState] = useGlobal();
  const { t } = useTranslation();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  const onInventoryRoute = location?.pathname?.startsWith('/inventory');
  const isOffline = globalState.networkState === 'offline';

  if (!isOffline || onInventoryRoute) return null;

  return (
    <Alert
      severity="warning"
      variant="outlined"
      icon={false}
      sx={{
        mt: 2,
        mx: 'auto',
        width: 'calc(100% - 64px)',
        maxWidth: 600,
        p: 0.75,
        borderRadius: 3,
        borderColor: 'warning.main',
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'warning.900' : 'warning.50'),
        '& .MuiAlert-message': { width: '100%', py: 0 },
      }}
    >
      <Button
        color="inherit"
        aria-expanded={expanded}
        aria-controls="offline-banner-details"
        sx={{
          display: 'grid',
          gridTemplateColumns: '32px minmax(0, 1fr) 32px',
          alignItems: 'center',
          justifyItems: 'center',
          width: '100%',
          minHeight: 36,
          p: 0.5,
          textTransform: 'none',
          borderRadius: 2,
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <CloudOffRoundedIcon sx={{ color: 'warning.dark' }} />
        <Typography
          component="span"
          variant="subtitle2"
          sx={{ fontWeight: 700, color: 'warning.dark', lineHeight: 1.2 }}
        >
          {t('offline')}
        </Typography>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Button>
      <Collapse id="offline-banner-details" in={expanded} sx={{ width: '100%' }}>
        <Box sx={{ textAlign: 'left', pt: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
            {t('offline_title')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('offline_more')}
          </Typography>
          <Typography variant="body2">
            {t('offline_sync_reminder')}
          </Typography>
        </Box>
      </Collapse>
    </Alert>
  );
}

function MainPageContent(props) {
  const { data: signInCheckResult } = useSignInCheck();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const db = useFirestore();
  const analytics = getAnalytics();

  useEffect(() => {
    if (!signInCheckResult?.signedIn === true
      && location.pathname !== '/contact'
      && location.pathname !== '/cgu'
      && location.pathname !== '/cgv'
      && location.pathname !== '/cgu-cgv'
      && location.pathname !== '/privacy-policy') {
      navigate('/signin');
    }
  }, [location.pathname, navigate, signInCheckResult?.signedIn]);

  const sendEmail = async (values) => {
    const emailContent = `Email: 
      ${values.email}

      Nom et entreprise: 
      ${values.nameAndCompany}

      Message: 
      ${values.message}`;

    await addDoc(
      collection(db, 'mail'),
      {
        to: 'contact@labox-apps.com',
        message: {
          subject: `[Inventory, Contact] ${values.nameAndCompany}`,
          text: emailContent,
        },
      },
    );
  };

  const privacyPolicy = () => {
    if (t('locale') === 'fr') {
      return privacyFR;
    }
    return privacyEN;
  };

  const access = userInventoryAppAccess(globalState.accessGrantNames);

  if (['initial', 'loading'].includes(globalState.userStatus)) {
    return <LoadingPage />;
  }

  if (globalState.userStatus !== 'setupDone' || access === 'none') {
    return (
      <Routes>
        <Route path="/" element={<SelectInventory />} />
        <Route
          path="pdfViewer/*"
          element={<StoragePDFViewerPage />}
        />
        <Route exact path="account" element={<MyAccount />} />
        <Route exact path="signin" element={<SignInPage />} />
        <Route
          path="contact"
          element={(
            <Contact
              t={t}
              navigate={navigate}
              globalActions={globalActions}
              analytics={analytics}
              sendEmail={sendEmail}
              signInCheckResult={signInCheckResult}
            />
          )}
        />
        <Route path="cgu" element={<PDFView path={cgu} />} />
        <Route path="cgv" element={<PDFView path={cgv} />} />
        <Route path="privacy-policy" element={<PDFView path={privacyPolicy()} />} />
        <Route path="cgu-cgv" element={<PDFView path={cgucgv} />} />
        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
    );
  }

  if (access === 'full') {
    return <FullInventorySolutionRoutes sendEmail={sendEmail} privacyPolicy={privacyPolicy} />;
  }
  return <OnlyCalculationsSolutionRoutes sendEmail={sendEmail} privacyPolicy={privacyPolicy} />;
}

function FullInventorySolutionRoutes({ sendEmail, privacyPolicy }) {
  const { data: signInCheckResult } = useSignInCheck();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const analytics = getAnalytics();

  return (
    <Routes>
      <Route path="/" element={<SelectInventory />} />
      <Route path="inventory" element={<Inventory />} />
      <Route path="inventory/operation/:containerId" element={<Operation />} />
      <Route
        path="pdfViewer/*"
        element={<StoragePDFViewerPage />}
      />
      <Route exact path="account" element={<MyAccount />} />
      <Route exact path="signin" element={<SignInPage />} />
      <Route
        path="contact"
        element={(
          <Contact
            t={t}
            navigate={navigate}
            globalActions={globalActions}
            analytics={analytics}
            sendEmail={sendEmail}
            signInCheckResult={signInCheckResult}
          />
          )}
      />
      <Route path="cgu" element={<PDFView path={cgu} />} />
      <Route path="cgv" element={<PDFView path={cgv} />} />
      <Route path="privacy-policy" element={<PDFView path={privacyPolicy()} />} />
      <Route path="cgu-cgv" element={<PDFView path={cgucgv} />} />
      <Route element={<AdminOnlineGate />}>
        <Route element={<AdminAccessGate />}>
          <Route path="admin" element={<Admin />} />
          <Route element={<AdminAccessGate requireEdit />}>
            <Route path="admin/createInventory" element={<CreateInventory />} />
          </Route>
          <Route path="admin/inventories">
            <Route index element={<Inventories />} />
            <Route path=":inventoryId" element={<InventoryDetail />} />
            <Route path=":inventoryId/history" element={<InventoryHistory />} />
          </Route>
          <Route path="admin/incidents" element={<Incidents />} />
        </Route>
      </Route>
      <Route
        path="*"
        element={<NotFound />}
      />
    </Routes>
  );
}

function OnlyCalculationsSolutionRoutes({ sendEmail, privacyPolicy }) {
  const { data: signInCheckResult } = useSignInCheck();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const analytics = getAnalytics();

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route exact path="account" element={<MyAccount />} />
      <Route exact path="signin" element={<SignInPage />} />
      <Route
        path="contact"
        element={(
          <Contact
            t={t}
            navigate={navigate}
            globalActions={globalActions}
            analytics={analytics}
            sendEmail={sendEmail}
            signInCheckResult={signInCheckResult}
          />
        )}
      />
      <Route path="cgu" element={<PDFView path={cgu} />} />
      <Route path="cgv" element={<PDFView path={cgv} />} />
      <Route path="privacy-policy" element={<PDFView path={privacyPolicy()} />} />
      <Route path="cgu-cgv" element={<PDFView path={cgucgv} />} />
      <Route element={<AdminOnlineGate />}>
        <Route element={<AdminAccessGate />}>
          <Route path="admin" element={<Admin />} />
          <Route element={<AdminAccessGate requireEdit />}>
            <Route path="admin/createInventory" element={<CreateInventory />} />
          </Route>
          <Route path="admin/inventories">
            <Route index element={<Inventories />} />
            <Route path=":inventoryId" element={<InventoryDetail />} />
            <Route path=":inventoryId/history" element={<InventoryHistory />} />
          </Route>
          <Route path="admin/incidents" element={<Incidents />} />
        </Route>
      </Route>
      <Route
        path="*"
        element={<NotFound />}
      />
    </Routes>
  );
}
